import { useCallback, useEffect, useState } from 'react'
import { Icon, StatusBadge, DueBadge } from '@carrent/ui'
import { api } from '@carrent/api'
import {
  CAR_BODY_TYPES,
  CAR_STATUS_OPTIONS,
  describeCarDue,
} from '@carrent/domain'
import { RegionBadge } from '../../components/RegionPicker'
import { AuthorshipLine, CarChangeList } from './CarChangeList'

/**
 * Сводка машины в боковой панели, с правкой на месте.
 *
 * ── Что здесь редактируется и почему не всё ───────────────────────────
 *
 * Поля, которые диспетчер меняет по ходу смены: статус, пробег, год,
 * цвет, тип кузова, комментарий. Их правка — обычное `PATCH` по карточке,
 * и она попадает в историю ручных изменений наравне с правкой из карточки.
 *
 * Регион здесь не редактируется намеренно. `PATCH /api/admin/cars/<id>`
 * его и не принимает: перенос между регионами — отдельный процесс
 * в routers/regions.py, он пишется как `car.transferred` и несёт свои
 * проверки. Дать сменить регион отсюда значило бы обойти и то и другое.
 *
 * Госномер и VIN тоже не здесь: они уникальны, их правка может упереться
 * в 409 «уже занят», и разбираться с этим в узкой панели неудобно.
 * Для них есть карточка.
 *
 * ── Про пробег ────────────────────────────────────────────────────────
 *
 * Правка вниз разрешена: это исправление опечатки, и оно должно быть
 * возможно. Поездки и ремонты уменьшить пробег не могут — там своя
 * защита, — но ручная правка на то и ручная. Уменьшение видно в истории
 * с автором и прежним значением, и это правильный способ контроля:
 * запретить исправление ошибки хуже, чем показать, кто его сделал.
 */

const EDITABLE = ['status', 'mileage', 'year', 'color', 'body_type', 'comment']

// Сколько правок показывать в панели. Десять — примерно то, что помещается
// без прокрутки поверх сводки; за большим есть вкладка на карточке.
const HISTORY_LIMIT = 10

export function CarPreview({ car, canEdit = false, onSaved, onActor, dictionaries = {} }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [history, setHistory] = useState([])
  const [authorship, setAuthorship] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/admin/cars/${car.id}/history`, {
        params: { page: 1, per_page: HISTORY_LIMIT },
      })
      setHistory(r.data?.data?.items || [])
      setAuthorship(r.data?.data?.authorship || null)
    } catch {
      // История — дополнение к сводке, а не сама сводка. Её отсутствие
      // не повод закрыть панель: у менеджера чужого региона запрос
      // вернёт 403, и это не ошибка карточки.
      setHistory([])
      setAuthorship(null)
    }
  }, [car.id])

  // Выбор другой машины в списке не должен оставлять открытой форму
  // с полями предыдущей: панель одна, а машина в ней меняется.
  useEffect(() => {
    setEditing(false)
    setError('')
    setHistoryOpen(false)
    loadHistory()
  }, [car.id, loadHistory])

  const start = () => {
    setForm({
      status: car.status || 'available',
      mileage: car.mileage ?? '',
      year: car.year ?? '',
      color: car.color || '',
      body_type: car.body_type || 'other',
      comment: car.comment || '',
    })
    setError('')
    setEditing(true)
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      // Отправляются только изменившиеся поля: сервер пишет в историю
      // разницу, и лишнее поле в запросе создало бы запись о правке,
      // которой не было.
      const patch = {}
      for (const key of EDITABLE) {
        const next = form[key] === '' ? null : form[key]
        const current = car[key] ?? null
        if (String(next ?? '') !== String(current ?? '')) patch[key] = next
      }

      if (Object.keys(patch).length === 0) {
        setEditing(false)
        return
      }

      const { data } = await api.patch(`/admin/cars/${car.id}`, patch)
      setEditing(false)
      onSaved?.(data?.data)
      // Без этого собственная правка не появляется в списке изменений,
      // и панель выглядит так, будто сохранения не было.
      await loadHistory()
    } catch (e) {
      setError(e?.response?.data?.message || 'Не удалось сохранить изменения')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="preview">
        {error && <div className="alert alert--error">{error}</div>}

        <div className="preview__form">
          <Field label="Статус">
            <select className="input" value={form.status} onChange={set('status')}>
              {CAR_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Пробег, км">
            <input className="input" type="number" min="0"
              value={form.mileage} onChange={set('mileage')} />
          </Field>

          <Field label="Год">
            <input className="input" type="number" value={form.year} onChange={set('year')} />
          </Field>

          <Field label="Цвет">
            <input className="input" value={form.color} onChange={set('color')} />
          </Field>

          <Field label="Кузов">
            <select className="input" value={form.body_type} onChange={set('body_type')}>
              {CAR_BODY_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Комментарий">
            <input className="input" value={form.comment} onChange={set('comment')} />
          </Field>
        </div>

        <p className="preview__hint">
          Госномер, VIN и регион меняются в карточке: у номера и VIN своя
          проверка на занятость, у региона — отдельный перенос.
        </p>

        <div className="preview__actions">
          <button type="button" className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Сохранить'}
          </button>
          <button type="button" className="btn btn--ghost"
            onClick={() => setEditing(false)} disabled={saving}>
            Отмена
          </button>
        </div>
      </div>
    )
  }

  const ops = car.ops || {}
  const due = describeCarDue(ops.due)
  const position = ops.position

  return (
    <div className="preview">
      <div className="preview__badges">
        <StatusBadge status={car.status} />
        {car.region && <RegionBadge region={car.region} />}
        {due && <DueBadge due={due} />}
      </div>

      <AuthorshipLine authorship={authorship} onActor={onActor} />

      {canEdit && (
        <button type="button" className="btn btn--secondary btn--sm preview__edit" onClick={start}>
          <Icon name="edit" size={14} />
          Изменить
        </button>
      )}

      <dl className="preview__facts">
        <Fact label="Водитель">
          {ops.driver ? ops.driver.full_name : <span className="table__muted">Свободна</span>}
        </Fact>

        <Fact label="Последние данные">
          {position ? (
            <a
              className="preview__link"
              href={`https://www.openstreetmap.org/?mlat=${position.lat}&mlon=${position.lng}#map=15/${position.lat}/${position.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="map" size={13} />
              {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
            </a>
          ) : (
            <span className="table__muted">Координат нет</span>
          )}
        </Fact>

        <Fact label="Открытые ремонты">
          {ops.open_repairs > 0 ? ops.open_repairs : <span className="table__muted">Нет</span>}
        </Fact>

        <Fact label="Пробег">
          {car.mileage ? `${car.mileage.toLocaleString('ru')} км` : <span className="table__muted">—</span>}
        </Fact>

        <Fact label="Год">{car.year || <span className="table__muted">—</span>}</Fact>

        <Fact label="Кузов">
          {car.body_type && car.body_type !== 'other' && car.body_type_label
            ? car.body_type_label
            : <span className="table__muted">Не указан</span>}
        </Fact>

        {car.comment && <Fact label="Комментарий">{car.comment}</Fact>}
      </dl>

      {history.length > 0 && (
        <section className="preview__history">
          <button type="button" className="preview__history-head"
            aria-expanded={historyOpen} onClick={() => setHistoryOpen(o => !o)}>
            <Icon name="chevronDown" size={14}
              className={historyOpen ? 'history__caret history__caret--open' : 'history__caret'} />
            Последние изменения
            <span className="preview__history-count">{history.length}</span>
          </button>

          {historyOpen && (
            <>
              <CarChangeList entries={history} dictionaries={dictionaries} compact />
              {/* Оговорка та же, что на вкладке: изменения от поездок сюда
                  не попадают, и без пояснения список читается как полный. */}
              <p className="preview__hint">
                Только ручные правки. Статус на выезде и пробег по возвращении
                меняются поездкой и здесь не показываются.
              </p>
            </>
          )}
        </section>
      )}
    </div>
  )
}

function Fact({ label, children }) {
  return (
    <div className="preview__fact">
      <dt className="preview__fact-label">{label}</dt>
      <dd className="preview__fact-value">{children}</dd>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="preview__field">
      <span className="preview__field-label">{label}</span>
      {children}
    </label>
  )
}
